import React, { useMemo } from "react";
import { map } from "lodash-es";

import { Spacer } from "shared/layout";
import { Card, FancyLink, Message, RTEContent } from "shared/components";
import { useStagePageContext, useVenuePageContext } from "Venues/contexts";
import { IStatus } from "shared/types";
import { IEquipmentCategoryItem } from "Venues/types";
import { VENUE_PATHS } from "Venues/router";

import { copy } from "shared/assets";
import { EquipmentBlockSubcategory } from "./EquipmentBlockSubcategory";
import { getEquipmentBlockActions, useVenueRoute } from "Venues/utils";
import { Equipment } from "shared/assets/icons";
const { equipmentCategory } = copy;

interface EquipmentBlockProps {
  category: IEquipmentCategoryItem;
}

export const EquipmentBlock: React.FC<EquipmentBlockProps> = ({ category }) => {
  const { pushRoute, getPath } = useVenueRoute();
  const { venueId, brandColor } = useVenuePageContext();
  const { stageId, userStagePermissions, openSuggestStageEditModal } =
    useStagePageContext();

  const actions = useMemo(() => {
    if (stageId) {
      return getEquipmentBlockActions({
        userPermissions: userStagePermissions,
        venueId,
        stageId,
        categorySlug: category.slug,
        openSuggestStageEditModal,
        pushRoute,
      });
    }
  }, [
    stageId,
    venueId,
    userStagePermissions,
    category,
    openSuggestStageEditModal,
    pushRoute,
  ]);

  const showEmptyCta = useMemo(() => {
    // if subcategory has text, don't show empty CTA
    const subcategoryTexts = map(category.categories, "text").filter(
      (text) => !!text // remove undefined from array
    );

    return (
      !category?.text &&
      !category?.amount &&
      !subcategoryTexts.length &&
      userStagePermissions.EDIT
    );
  }, [category, userStagePermissions]);

  if (stageId === null) return null;

  return (
    <Card
      id={category.slug}
      accentColor={brandColor}
      title={category.label}
      icon={Equipment}
      actions={actions}
      actionDataTestId={`${category.category}_action_select`}
    >
      <Spacer size="l">
        {showEmptyCta && (
          <Message status={IStatus.warning}>
            {equipmentCategory.empty}{" "}
            <FancyLink
              to={getPath(VENUE_PATHS.EDIT_STAGE_EQUIPMENT, {
                stageId,
                params: { categorySlug: category.slug },
              })}
              status={IStatus.warning}
            >
              {equipmentCategory.emptyCta}
            </FancyLink>
          </Message>
        )}
        {!showEmptyCta && (
          <Spacer size="xl">
            <RTEContent content={category.text} />
            {category.categories.map((subcategory) => (
              <EquipmentBlockSubcategory
                key={`${stageId}${subcategory.category}`}
                subcategory={subcategory}
              />
            ))}
          </Spacer>
        )}
      </Spacer>
    </Card>
  );
};
